import React, { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ProjectsContext, ProjectsContextProps } from "./projects-context-props.ts";
import { fetchItem, postItem } from "@/queries/queries-helper.ts";
import { PagedDTO } from "@/models/interfaces/dtos/paged-dto.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import BaamerProject from "@/models/classes/baamer-project.ts";
import BaamerProjectDto from "@/models/interfaces/dtos/baamer-project-dto.ts";
import ProjectLock from "@/models/classes/project-lock.ts";
import FlowMode from "@/models/enums/flow-mode.ts";

const ProjectsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

    const [projects, setProjects] = useState<BaamerProject[] | undefined>(undefined);

    const createProjectMutation = useMutation(
        async ({ name, localisationId, flowMode }: { name: string, localisationId: number, flowMode: FlowMode }) =>
            postItem<BaamerProjectDto>(ENDPOINTS.PROJECTS, {
                name,
                localisationId,
                flowMode
            }),
        {
            onSuccess: (newProjectDto) => {
                if (newProjectDto) {
                    setProjects(prevState => {
                        if (prevState === undefined) {
                            return [new BaamerProject(newProjectDto)];
                        }
                        return [
                            ...prevState,
                            new BaamerProject(newProjectDto)
                        ];
                    });
                }
            },
            onError: (error) => {
                throw error;
            }
        }
    );

    const { isLoading, error } = useQuery(
        "projects",
        async () => {
            const fetchedProjects = await fetchItem<PagedDTO<BaamerProjectDto>>(ENDPOINTS.PROJECTS);
            if (fetchedProjects == null) return;

            setProjects(fetchedProjects.data.map(project => new BaamerProject(project)));
        }, {
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000
    }
    )

    /* RAFRAICHISSEMENT IDS */

    const refetchProjectIds = useCallback(async (projectId: string) => {
        const onlineProject = await fetchItem<BaamerProjectDto>(ENDPOINTS.GET_PROJECT(projectId));
        if (onlineProject == null)
            return;

        setProjects(prev => {
            if (prev == null) return prev;
            const next = [...prev];
            const target = next.find(p => p.id === projectId);
            if (target) {
                target.updateFromDto(onlineProject);
            }
            return next;
        })
    }, []);

    /* GESTION DU LOCK */

    const hydrateProjectLock = useCallback((projectId: string, projectLock: ProjectLock) => {
        setProjects(prev => {
            if (prev == null) return prev;
            const next = [...prev];
            const target = next.find(p => p.id === projectId);
            if (target) {
                target.setProjectLock(projectLock);
            }
            return next;
        })
    }, [])

    const lockProject = useCallback((projectId: string) => {
        setProjects(prev => {
            if (prev == null) return prev;
            const next = [...prev];
            const target = next.find(p => p.id === projectId);
            if (target) {
                target.lockProject();
            }
            return next;
        })
    }, [])

    const unlockProject = useCallback((projectId: string) => {
        setProjects(prev => {
            if (prev == null) return prev;
            const next = [...prev];
            const target = next.find(p => p.id === projectId);
            if (target) {
                target.unlockProject();
            }
            return next;
        })
    }, [])

    const contextProps: ProjectsContextProps = {
        projects,
        isLoading,
        error,

        createProjectMutation,

        refetchProjectIds,

        hydrateProjectLock,
        lockProject,
        unlockProject
    }

    return (
        <ProjectsContext.Provider value={contextProps}>
            {children}
        </ProjectsContext.Provider>
    )
}

export default ProjectsContextProvider;